import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RepairItem } from "@/types";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { generateTicketQRCode } from "@/utils/qrCodeUtils";
import { formatDistanceToNow } from "date-fns";
import { Phone, FileText, CreditCard, AlertCircle } from "lucide-react";

interface RepairCardProps {
  repair: RepairItem;
}

const statusColors = {
  pending:
    "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-300 dark:border-yellow-700",
  inProgress:
    "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700",
  completed:
    "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700",
  cancelled:
    "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700",
  returned:
    "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/50 dark:text-purple-300 dark:border-purple-700",
};

const paymentStatusColors = {
  paid: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700",
  partial:
    "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700",
  unpaid:
    "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700",
};

const RepairCard: React.FC<RepairCardProps> = ({ repair }) => {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const generateQR = async () => {
      try {
        // Use ticket number for QR code if available, otherwise fall back to repair ID
        const ticketNumber = repair.ticketNumber || repair.id;
        const qrDataUrl = await generateTicketQRCode(ticketNumber);
        setQrCode(qrDataUrl);
      } catch (error) {
        console.error("Failed to generate QR code:", error);
      }
    };

    generateQR();
  }, [repair.id, repair.ticketNumber]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: "TND",
    }).format(amount);
  };

  const getStatusText = (status: string) => {
    return t(`repair.status.${status}`);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg">{repair.customerName}</CardTitle>
            <p className="text-sm text-gray-500">{repair.customerPhone}</p>
          </div>
          <Badge className={statusColors[repair.status]}>
            {getStatusText(repair.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="flex justify-between items-start">
          <div className="space-y-3 flex-1">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-gray-500" />
              <span className="text-sm">{repair.phoneModel}</span>
            </div>

            <div className="flex items-start space-x-2">
              <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
              <p className="text-sm text-gray-600 line-clamp-2">
                {repair.problemDescription}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <CreditCard className="h-4 w-4 text-gray-500" />
              <div className="flex flex-wrap gap-2 items-center">
                {repair.priceModifications &&
                repair.priceModifications.length > 0 ? (
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium">
                      {formatCurrency(
                        repair.repairPrice +
                          repair.priceModifications.reduce(
                            (sum, mod) => sum + mod.amount,
                            0
                          )
                      )}
                    </span>
                    <span
                      className="inline-flex items-center cursor-help"
                      title={`${t("repair.priceModified")}\n${t(
                        "repair.initialPrice"
                      )}: ${formatCurrency(
                        repair.repairPrice
                      )}\n${repair.priceModifications
                        .map(
                          (mod) =>
                            `${mod.reason}: ${formatCurrency(mod.amount)}`
                        )
                        .join("\n")}`}
                    >
                      <AlertCircle className="h-3 w-3 text-blue-600 ml-1" />
                    </span>
                  </div>
                ) : (
                  <span className="text-sm font-medium">
                    {formatCurrency(repair.repairPrice)}
                  </span>
                )}
                <Badge className={paymentStatusColors[repair.paymentStatus]}>
                  {t(`repair.payment.${repair.paymentStatus}`)}
                </Badge>
                {repair.downPayment > 0 && (
                  <span className="text-xs text-gray-500">
                    {t("repair.downPayment")}:{" "}
                    {formatCurrency(repair.downPayment)}
                  </span>
                )}
                {(repair.repairCost && repair.repairCost > 0) ||
                (repair.costModifications &&
                  repair.costModifications.length > 0) ? (
                  <div className="flex items-center gap-1">
                    {repair.costModifications &&
                    repair.costModifications.length > 0 ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          {t("repair.repairCost")}:{" "}
                          {formatCurrency(
                            (repair.repairCost || 0) +
                              repair.costModifications.reduce(
                                (sum, mod) => sum + mod.amount,
                                0
                              )
                          )}
                        </span>
                        <span
                          className="inline-flex items-center cursor-help"
                          title={`${t("repair.costModified")}\n${t(
                            "repair.repairCost"
                          )}: ${formatCurrency(
                            repair.repairCost || 0
                          )}\n${repair.costModifications
                            .map(
                              (mod) =>
                                `${mod.reason}: ${formatCurrency(mod.amount)}`
                            )
                            .join("\n")}`}
                        >
                          <AlertCircle className="h-3 w-3 text-amber-600 ml-1" />
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        {t("repair.repairCost")}:{" "}
                        {formatCurrency(repair.repairCost || 0)}
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {qrCode && (
            <div className="ml-4">
              <img
                src={qrCode}
                alt="QR Code"
                className="w-24 h-24 object-contain"
              />
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="pt-2 flex justify-between">
        <div className="text-xs text-gray-500">
          {t("repair.addedAgo", {
            time: formatDistanceToNow(new Date(repair.createdAt), {
              addSuffix: true,
            }),
          })}
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/repair/${repair.id}`}>{t("repair.viewDetails")}</Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default RepairCard;
